import React from "react";
var __jsx = React.createElement;
import { useTranslation } from "react-i18next";
import { UnstyledButton } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag, faEnvelope, faSquareCheck, faRectangleList, faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { useComposerStore } from "@/stores/composers";
import { useTaskDetailStore } from "@/stores/task-detail";
import { usePatientTagsStore } from "@/stores/patient-tags";
import { usePatientTasksStore } from "@/stores/patient-tasks";
import { useTaskActions } from "@/components/task-actions-provider";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
/** Override action grid — each tile minimises the task and navigates to where that action is performed. */
export function ActionPicker(_ref) {
  var onBack = _ref.onBack,
    isBackLoading = _ref.isBackLoading,
    _ref$canGoBack = _ref.canGoBack,
    canGoBack = _ref$canGoBack === void 0 ? true : _ref$canGoBack;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var openComposer = useComposerStore(function (s) {
    return s.open;
  });
  var minimizeTaskDetail = useTaskDetailStore(function (s) {
    return s.minimize;
  });
  var expandTags = usePatientTagsStore(function (s) {
    return s.setExpanded;
  });
  var startCreateTask = usePatientTasksStore(function (s) {
    return s.startCreate;
  });
  var overrides = useTaskActions();
  var handleUpdateTags = function handleUpdateTags() {
    trackEvent(ANALYTICS_EVENTS.ACTION_PICKER_UPDATE_TAGS);
    if (overrides.onUpdateTags) {
      overrides.onUpdateTags();
      return;
    }
    minimizeTaskDetail();
    expandTags(true);
  };
  var handleGenerateCommunication = function handleGenerateCommunication() {
    trackEvent(ANALYTICS_EVENTS.ACTION_PICKER_GENERATE_COMMUNICATION);
    if (overrides.onGenerateCommunication) {
      overrides.onGenerateCommunication();
      return;
    }
    minimizeTaskDetail();
    openComposer({
      id: "action-centre-override-letter",
      type: "letter",
      title: t("label.generate_letters", {
        defaultValue: "Generate letters"
      })
    });
  };
  var handleCreateTask = function handleCreateTask() {
    trackEvent(ANALYTICS_EVENTS.ACTION_PICKER_CREATE_TASK);
    if (overrides.onCreateTask) {
      overrides.onCreateTask();
      return;
    }
    minimizeTaskDetail();
    startCreateTask();
  };
  var handleCompleteForm = function handleCompleteForm() {
    trackEvent(ANALYTICS_EVENTS.ACTION_PICKER_COMPLETE_FORM);
    if (overrides.onCompleteForm) {
      overrides.onCompleteForm();
      return;
    }
    minimizeTaskDetail();
    openComposer({
      id: "action-centre-override-form",
      type: "form",
      title: t("label.complete_form", {
        defaultValue: "Complete form"
      })
    });
  };
  var handleAddNote = function handleAddNote() {
    trackEvent(ANALYTICS_EVENTS.ACTION_PICKER_ADD_NOTE);
    if (overrides.onAddNote) {
      overrides.onAddNote();
      return;
    }
    minimizeTaskDetail();
    openComposer({
      id: "action-centre-override-note",
      type: "note",
      title: t("label.add_note", {
        defaultValue: "Add note"
      })
    });
  };
  var tileClass = "flex items-center gap-3 px-4 py-3 rounded-sds-200 border border-sds-base-default bg-sds-base-default hover:border-sds-brand-default hover:bg-sds-brand-tertiary transition-colors";
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-4"
  }, /*#__PURE__*/React.createElement("p", {
    className: "text-style-body-small text-sds-base-secondary"
  }, t("label.actions_override_hint", {
    defaultValue: "Pick an action — the task minimises and takes you to where that action is performed."
  })), /*#__PURE__*/React.createElement("div", {
    className: "grid grid-cols-2 gap-3"
  }, /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "action-picker-update-tags",
    onClick: handleUpdateTags,
    className: tileClass
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faTag,
    className: "w-4 h-4 text-sds-brand-default flex-shrink-0"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small-strong text-sds-base-default"
  }, t("label.update_tags", {
    defaultValue: "Update tags"
  }))), /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "action-picker-generate-communication",
    onClick: handleGenerateCommunication,
    className: tileClass
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faEnvelope,
    className: "w-4 h-4 text-sds-brand-default flex-shrink-0"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small-strong text-sds-base-default"
  }, t("label.generate_letters", {
    defaultValue: "Generate letters"
  }))), /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "action-picker-create-task",
    onClick: handleCreateTask,
    className: tileClass
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faSquareCheck,
    className: "w-4 h-4 text-sds-brand-default flex-shrink-0"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small-strong text-sds-base-default"
  }, t("label.create_task", {
    defaultValue: "Create task"
  }))), /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "action-picker-complete-form",
    onClick: handleCompleteForm,
    className: tileClass
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faRectangleList,
    className: "w-4 h-4 text-sds-brand-default flex-shrink-0"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small-strong text-sds-base-default"
  }, t("label.complete_form", {
    defaultValue: "Complete form"
  })))), canGoBack && /*#__PURE__*/React.createElement("div", {
    className: "flex justify-end"
  }, /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "action-picker-back",
    disabled: isBackLoading,
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.ACTION_PICKER_BACK);
      onBack();
    },
    className: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sds-200 text-style-body-small-strong text-sds-brand-default hover:bg-sds-brand-tertiary active:bg-sds-brand-secondary transition-colors disabled:opacity-50"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faArrowLeft,
    className: "w-3 h-3"
  }), t("button.back_to_suggested_decision", {
    defaultValue: "Back to suggested decision"
  }))));
}